/** tm/legal/LegalFeedList.tsx — 2열 목록 (정부지원사업 목록 칼럼과 같은 모양): 검색 · 세부 주제 칩(가로 스크롤) · 카드(↑/↓ 이동 · Enter 열기) · 더 보기 */
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { C, T, R, IMEText } from '../components/ui';
import { btn, chip, num, TAB_HINT, MUTED } from './legalTheme';
import LegalFeedCard, { CardSkeleton } from './LegalFeedCard';
import type { FeedRow, FeedTab, LawKind, Selection, Subtopic } from './legalTypes';
import type { FeedState } from './useLegalFeed';

interface Props {
  feed: FeedState; tab: FeedTab; q: string; onlyNew: boolean; sel: Selection; mobile: boolean; starred: boolean;
  banner?: ReactNode; lawKind: LawKind | ''; subs: Subtopic[]; subCounts?: Record<string, number>; sub: string; top?: ReactNode;
  onQ: (v: string) => void; onSub: (s: string) => void; onSelect: (r: FeedRow) => void;
  /** 「모두 읽음」(2026-10-27 지도사 「모두 읽기 버튼 넣어놔줘」) — 새 소식 건수 · 누르면 새 소식 전부 읽음(되돌리기 토스트는 useReadAll) */
  unreadTotal?: number; onReadAll?: () => Promise<void>;
}

const PLACEHOLDER: Partial<Record<FeedTab, string>> = { news: '기사 제목·언론사', bill: '법안명·위원회·발의자', notice: '법령명·부처', cabinet: '법령명·부처', law: '법령명' };
const EMPTY_HEAD: Record<FeedTab, string> = {
  all: '아직 모인 글이 없습니다', law: '법령 개정이 아직 없습니다', prec: '판례·심판이 아직 없습니다', expc: '유권해석이 아직 없습니다',
  bill: '국회 법안이 아직 없습니다', notice: '입법예고가 아직 없습니다', cabinet: '국무회의를 통과한 법령안이 아직 없습니다', news: '뉴스가 아직 없습니다',
};

export default function LegalFeedList(p: Props) {
  const { feed, tab, q, onlyNew, sel, mobile, starred, banner, lawKind, subs, sub } = p;
  const [readBusy, setReadBusy] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const chipsRef = useRef<HTMLDivElement>(null);
  const [chipEnd, setChipEnd] = useState(true);
  const chipCheck = () => { const el = chipsRef.current; setChipEnd(!el || el.scrollLeft + el.clientWidth >= el.scrollWidth - 4); };
  useEffect(chipCheck, [subs]);
  const cards = () => Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('button.lw-card') || []);
  const onKey = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const list = cards();
    if (!list.length) return;
    e.preventDefault();
    const i = list.indexOf(document.activeElement as HTMLButtonElement);
    const next = i < 0 ? 0 : Math.max(0, Math.min(list.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1)));
    list[next].focus(); list[next].scrollIntoView({ block: 'nearest' });
  };
  const firstLoad = feed.loading && feed.rows.length === 0;
  const searching = !!q.trim();
  const emptyHead = starred ? '별표한 글이 없습니다' : onlyNew ? '확인할 새 소식이 없습니다' : searching || sub ? '조건에 맞는 글이 없습니다'
    : lawKind ? `${lawKind} 개정이 없습니다` : EMPTY_HEAD[tab];
  const emptyHint = starred ? '상세 화면에서 ☆ 별표를 누르면 여기에 모입니다.' : onlyNew ? '매일 새벽 4시에 새로 모읍니다.'
    : searching || sub ? '검색어를 줄이거나 세부 주제를 풀어 보세요.' : lawKind ? '법령 종류 「전체」를 눌러 보세요.' : TAB_HINT[tab];

  return (
    <section aria-label="목록" onKeyDown={onKey} style={{
      width: mobile ? '100%' : 280, flexShrink: 0, borderRight: mobile ? 0 : `1px solid ${C.border}`, overflowY: mobile ? 'visible' : 'auto',
      display: 'flex', flexDirection: 'column', minHeight: 0,
    }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 3, background: C.bg, padding: '12px 12px 8px' }}>
        {p.top}
        <IMEText type="search" value={q} onChange={p.onQ} aria-label="검색" className="lw-search"
          placeholder={`🔍 ${PLACEHOLDER[tab] || '제목·사건번호·기관'}`}
          onKeyDown={e => { if (e.key === 'ArrowDown' && !e.nativeEvent.isComposing) { e.preventDefault(); e.stopPropagation(); cards()[0]?.focus(); } }}
          style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.borderDk}`, fontSize: T.md, boxSizing: 'border-box', background: C.white, minHeight: mobile ? 42 : undefined, fontFamily: 'inherit' }} />
        {subs.length > 0 && (
          <div className={`lw-fade${chipEnd ? ' lw-end' : ''}`} style={{ marginTop: 8 }}>
          <div ref={chipsRef} onScroll={chipCheck} className="lw-noscroll" role="group" aria-label="세부 주제" title="옆으로 넘겨 더 보기" style={{ display: 'flex', gap: 5, overflowX: 'auto', paddingRight: chipEnd ? 0 : 24 }}>
            {subs.map(s => {
              const on = sub === s.label; const n = s.count ?? p.subCounts?.[s.label];
              return <button key={s.label} type="button" className="lw-f" aria-pressed={on} onClick={() => p.onSub(on ? '' : s.label)}
                style={{ ...chip(on), borderRadius: R.sm, padding: mobile ? '7px 10px' : '4px 9px', fontSize: T.sm }}>{s.label}{n != null ? ` ${num(n)}` : ''}</button>;
            })}
          </div>
          </div>
        )}
        {!firstLoad && feed.total > 0 && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: T.md, color: MUTED, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{num(feed.total)}건{!mobile && ' · ↑↓ 이동 · Enter 열기'}</span>
            {p.onReadAll && (p.unreadTotal || 0) > 0 && (
              <button type="button" disabled={readBusy} onClick={() => { if (!p.onReadAll) return; setReadBusy(true); p.onReadAll().finally(() => setReadBusy(false)); }}
                title={`새 소식 ${num(p.unreadTotal || 0)}건을 모두 읽음으로`}
                style={{ ...btn('sec'), flexShrink: 0, color: C.text, fontWeight: 700, cursor: readBusy ? 'wait' : 'pointer' }}>   {/* 이 화면의 다른 단추(원문 보기 · 번호 복사)와 같은 모양 */}
                {readBusy ? '읽는 중…' : `모두 읽음 ${num(p.unreadTotal || 0)}`}
              </button>
            )}
          </div>
        )}
      </div>
      {banner}
      <div ref={listRef} style={{ padding: '0 12px 12px' }} aria-busy={feed.loading}>
        {feed.error && (
          <div role="alert" style={{ marginBottom: 8, padding: '10px 12px', borderRadius: R.md, background: C.redLt, color: C.red, fontSize: T.sm }}>
            {feed.error} <button type="button" className="lw-f" onClick={feed.reload} style={{ ...btn(), padding: '2px 8px', marginLeft: 6 }}>다시 시도</button>
          </div>
        )}
        {feed.rows.map(r => <LegalFeedCard key={`${r.kind}${r.id}`} row={r} selected={!!sel && sel.kind === r.kind && sel.id === r.id} onClick={() => p.onSelect(r)} />)}
        {feed.loading && (firstLoad ? [0, 1, 2, 3, 4].map(i => <CardSkeleton key={i} />) : <CardSkeleton />)}
        {!feed.loading && feed.hasMore && (
          <button type="button" className="lw-f" onClick={feed.loadMore} style={{ ...btn(), width: '100%', justifyContent: 'center', minHeight: mobile ? 42 : undefined }}>
            더 보기 <span style={{ color: MUTED, fontWeight: 400, fontVariantNumeric: 'tabular-nums' }}>({num(feed.rows.length)}/{num(feed.total)})</span>
          </button>
        )}
        {!feed.loading && !feed.error && feed.rows.length === 0 && (
          <div style={{ padding: '32px 8px', textAlign: 'center', fontSize: T.sm, lineHeight: 1.7 }}>
            <div style={{ fontSize: 28, marginBottom: 6 }} aria-hidden="true">📚</div>
            <div style={{ color: C.sub, fontWeight: 600 }}>{emptyHead}</div>
            <div style={{ color: MUTED, fontSize: T.sm }}>{emptyHint}</div>
          </div>
        )}
      </div>
    </section>
  );
}
